import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './store-card.style.js'

import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { InternalStore, StoreModel } from 'components/discovery/store-locator/typing/store-locator.js'

import { STORE_LOCATOR_STORE, StoreLocatorStore } from 'components/discovery/store-locator/store-locator-store.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'


initializeIconsByRole({
  'csc-store-card-badge.icon': { name: 'star', style: 'solid' },
})

@customElement('csc-store-card')
export class CSCStoreCard extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(STORE_LOCATOR_STORE)
  storeLocatorStore!: StoreLocatorStore

  @property({ type: Object })
  store: InternalStore = new StoreModel().parseToInternal()

  @property({ type: Boolean, reflect: true })
  selected: boolean = false

  @state()
  shouldShowDistance = false

  constructor() {
    super()

    createEffect(() => {
      this.selected = this.store.id === this.storeLocatorStore.selectedStore()?.id
      this.shouldShowDistance = [
        'current_location',
        'autocomplete',
      ].includes(this.storeLocatorStore.mapCoordinates()?.type || '')
    })
  }

  stopPropagation = (e: Event) => {
    e.stopPropagation()
  }

  selectStore = () => {
    this.storeLocatorStore.selectStore(this.store)
  }

  renderPremiumBadge = () => html`
    <cse-badge variant="premium" size="small">
      <cse-icon icon-role="csc-store-card-badge.icon" slot="prefix"></cse-icon>
      ${t('store_card_badge_text', { ns: 'csc-store-locator', default: 'Premium' })}
    </cse-badge>
  `

  calculateDistanceForStore = () => {
    return this.store.distanceInM > 1000 ? `${this.store.distanceInKm} km` : `${this.store.distanceInM} m`
  }

  renderHeader = () => html`
    <cse-group justify="space-between" alignment="center">
      <cse-text id="title" variant="heading-6" as="h3">
        ${this.store.name}
      </cse-text>
      ${this.shouldShowDistance ? html`
        <cse-text id="distance" variant="caption-l">
          ${this.calculateDistanceForStore()}
        </cse-text>
      ` : nothing}
    </cse-group>
  `

  private shouldLinkWebsite = () => {
    return this.selected && this.store.websiteUrl !== ''
  }

  private renderStoreInfo = () => html`
    <div id="info">
      <cse-text id="street" variant="caption-l">
        ${this.store.address.street} ${this.store.address.houseNumber}
      </cse-text>
      <cse-text id="zip" variant="caption-l">
        ${this.store.address.zipcode} ${this.store.address.city}
      </cse-text>
      <cse-text id="country" variant="caption-l">
        ${this.store.address.country}
      </cse-text>
    </div>
  `

  private renderViewStoreButton = () => html`
    <cse-button
      url="${this.store.websiteUrl}"
      target="_blank"
      rel="noopener noreferrer"
      @click="${this.stopPropagation}"
      size="small"
      class="view-store-button"
    >
      ${t('view_store_button_text', { ns: 'csc-store-locator', default: 'View store' })}
    </cse-button>
  `

  override render() {
    return html`
      <cse-selectable
        class="csc-store-card"
        @click="${this.selectStore}"
        state="${this.selected ? 'selected' : 'selectable'}"
      >
        <cse-group direction="column">
          ${this.store.premium ? this.renderPremiumBadge() : nothing}
          ${this.renderHeader()}
          ${this.renderStoreInfo()}
          ${this.shouldLinkWebsite() ? this.renderViewStoreButton() : nothing}
        </cse-group>
      </cse-selectable>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-store-card': CSCStoreCard
  }
}
